import React, { useState, useEffect } from "react";
import { getCollaborateurs, createProjet, parseFicheDocx, confirmerImportFiche } from "../services/api";
import ModaleV2 from "./ModaleV2";
import SelecteurPersonnes from "./SelecteurPersonnes";
import { LIBELLES_ROLE_PROJET, dateCourte, nomCollaborateur } from "../services/projet";

// ===================================================================
// The Crowd v2 — Pilotage › Projets : nouveau projet
//
// Creation a la main, ou a partir d'une fiche projet .docx (import
// conserve) : l'analyse preremplit la fiche et ses etapes, rien n'est
// cree avant « Creer le projet ». Les roles sont affectes dans la meme
// modale ; le backend verifie l'ensemble (chef manager ou directeur,
// pas de cumul chef / principal sauf directeur, un role par personne).
//
// Props : collaborateur, onCree(projetId), onClose
// ===================================================================

const ROLES_CHEF = ["manager", "directeur"];

function versPersonne(c) {
  return { id: c.id, displayName: nomCollaborateur(c), email: c.email };
}

function ModaleNouveauProjetV2({ collaborateur, onCree, onClose }) {
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [valeurs, setValeurs] = useState({ nom: "", description: "", date_debut: "", date_fin_prevue: "" });
  const [chefId, setChefId] = useState(ROLES_CHEF.includes(collaborateur.role) ? String(collaborateur.id) : "");
  const [principalId, setPrincipalId] = useState("");
  const [cochefs, setCochefs] = useState([]);
  const [secondaires, setSecondaires] = useState([]);
  const [fichier, setFichier] = useState(null);
  const [apercu, setApercu] = useState(null); // resultat de l'analyse .docx
  const [analyse, setAnalyse] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    getCollaborateurs()
      .then(setCollaborateurs)
      .catch((err) => setErreur(`Collaborateurs non chargés : ${err.message}`));
  }, []);

  const changer = (champ) => (e) => setValeurs((v) => ({ ...v, [champ]: e.target.value }));

  const rechercher = async (texte) => {
    const t = texte.trim().toLowerCase();
    if (t.length < 2) return [];
    return collaborateurs
      .filter((c) => `${c.prenom} ${c.nom} ${c.email}`.toLowerCase().includes(t))
      .slice(0, 8)
      .map(versPersonne);
  };

  const analyser = async () => {
    if (!fichier) return;
    setAnalyse(true);
    setErreur(null);
    try {
      const p = await parseFicheDocx(fichier);
      setApercu(p);
      setValeurs({
        nom: p.nom || "",
        description: p.description || "",
        date_debut: dateCourte(p.date_debut),
        date_fin_prevue: dateCourte(p.date_fin_prevue),
      });
    } catch (err) {
      setErreur(`Analyse impossible : ${err.message}`);
    } finally {
      setAnalyse(false);
    }
  };

  const creer = async () => {
    if (valeurs.date_debut && valeurs.date_fin_prevue && valeurs.date_debut > valeurs.date_fin_prevue) {
      setErreur("La date de fin prévue précède la date de début.");
      return;
    }
    setEnCours(true);
    setErreur(null);
    const roles = {
      chef_projet_id: Number(chefId),
      acteur_principal_id: Number(principalId),
      cochef_ids: cochefs.map((p) => p.id),
      secondaire_ids: secondaires.map((p) => p.id),
    };
    const fiche = {
      nom: valeurs.nom.trim(),
      description: valeurs.description || null,
      date_debut: valeurs.date_debut || null,
      date_fin_prevue: valeurs.date_fin_prevue || null,
    };
    try {
      let projetId;
      if (apercu) {
        const r = await confirmerImportFiche({ projet: { ...apercu, ...fiche }, ...roles }, collaborateur.id);
        projetId = r.projet_id;
      } else {
        const r = await createProjet({ ...fiche, ...roles }, collaborateur.id);
        projetId = r.id;
      }
      onCree(projetId);
    } catch (err) {
      setErreur(err.message);
      setEnCours(false);
    }
  };

  const chefsPossibles = collaborateurs.filter((c) => ROLES_CHEF.includes(c.role));
  const complet = valeurs.nom.trim() && chefId && principalId;

  return (
    <ModaleV2
      titre="Nouveau projet"
      onClose={onClose}
      bloquee={enCours || analyse}
      actions={
        <>
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={onClose} disabled={enCours}>Annuler</button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={creer} disabled={enCours || !complet}>
            {enCours ? "Création…" : "Créer le projet"}
          </button>
        </>
      }
    >
      {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}

      <fieldset className="v2-groupe-champs">
        <legend className="v2-label">Depuis une fiche .docx (optionnel)</legend>
        <div className="v2-projet-import">
          <input type="file" accept=".docx" className="v2-champ-fichier" aria-label="Fiche projet .docx"
            onChange={(e) => { setFichier(e.target.files?.[0] || null); setApercu(null); }} />
          <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={analyser} disabled={!fichier || analyse}>
            {analyse ? "Analyse…" : "Analyser"}
          </button>
        </div>
        {apercu && (
          <p className="v2-champ-aide">
            Fiche lue : {apercu.etapes?.length || 0} étape{(apercu.etapes?.length || 0) > 1 ? "s" : ""} importée{(apercu.etapes?.length || 0) > 1 ? "s" : ""}
            {apercu.contexte || apercu.perimetre || apercu.livrable || apercu.risque ? ", contexte, périmètre, livrable et risques repris" : ""}.
            Vérifiez les champs ci-dessous.
          </p>
        )}
      </fieldset>

      <div className="v2-champ">
        <label className="v2-label" htmlFor="np-nom">Nom<span className="v2-requis">*</span></label>
        <input id="np-nom" value={valeurs.nom} onChange={changer("nom")} />
      </div>
      <div className="v2-champ">
        <label className="v2-label" htmlFor="np-description">Description</label>
        <textarea id="np-description" rows={3} value={valeurs.description} onChange={changer("description")} />
      </div>
      <div className="v2-champs-ligne">
        <div className="v2-champ">
          <label className="v2-label" htmlFor="np-debut">Début</label>
          <input id="np-debut" type="date" value={valeurs.date_debut} onChange={changer("date_debut")} />
        </div>
        <div className="v2-champ">
          <label className="v2-label" htmlFor="np-fin">Fin prévue</label>
          <input id="np-fin" type="date" value={valeurs.date_fin_prevue} onChange={changer("date_fin_prevue")} />
        </div>
      </div>

      <fieldset className="v2-groupe-champs">
        <legend className="v2-label">Rôles</legend>
        <div className="v2-champs-ligne">
          <div className="v2-champ">
            <label className="v2-label" htmlFor="np-chef">{LIBELLES_ROLE_PROJET.chef}<span className="v2-requis">*</span></label>
            <select id="np-chef" value={chefId} onChange={(e) => setChefId(e.target.value)}>
              <option value="">—</option>
              {chefsPossibles.map((c) => <option key={c.id} value={c.id}>{nomCollaborateur(c)}</option>)}
            </select>
          </div>
          <div className="v2-champ">
            <label className="v2-label" htmlFor="np-principal">{LIBELLES_ROLE_PROJET.principal}<span className="v2-requis">*</span></label>
            <select id="np-principal" value={principalId} onChange={(e) => setPrincipalId(e.target.value)}>
              <option value="">—</option>
              {collaborateurs.map((c) => <option key={c.id} value={c.id}>{nomCollaborateur(c)}</option>)}
            </select>
          </div>
        </div>
        <div className="v2-champ">
          <label className="v2-label" htmlFor="np-cochefs">Co-chefs</label>
          <SelecteurPersonnes id="np-cochefs" selection={cochefs} onChange={setCochefs} rechercher={rechercher}
            placeholder="Rechercher un collaborateur…" />
        </div>
        <div className="v2-champ">
          <label className="v2-label" htmlFor="np-secondaires">Acteurs secondaires</label>
          <SelecteurPersonnes id="np-secondaires" selection={secondaires} onChange={setSecondaires} rechercher={rechercher}
            placeholder="Rechercher un collaborateur…" />
        </div>
      </fieldset>
    </ModaleV2>
  );
}

export default ModaleNouveauProjetV2;
